import { IPaginationResponse } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import {
  IAbandonCheckoutRequest,
  IAbandonCheckoutResponse,
  IResumeCheckoutRequest,
  IResumeCheckoutResponse,
  ICreateOrderPayproRequest,
  ICreateOrderPayproResponse,
  IGetOrderDetailsRequest,
  IGetOrdersRequest,
  IGetOrderStatusRequest,
  IGetOrderStatusResponse,
  IGetPriceRequest,
  IGetPriceResponse,
  IOrderDetails,
  IOrdersSummary,
  IProgramForCompany,
} from '../../interfaces/purchases.interface'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

// @lat: [[state#Module purchase checkout]] [[state#Settings orders list]]
export const purchasesApi = createApi({
  reducerPath: 'purchasesApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/gateway/manager`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  endpoints: (builder) => ({
    getProgramForCompany: builder.mutation<IProgramForCompany[], void>({
      query: () => ({
        url: `/getProgramForCompany`,
        method: 'POST',
        body: {},
      }),
    }),
    createOrderPaypro: builder.mutation<ICreateOrderPayproResponse, ICreateOrderPayproRequest>({
      query: ({ totalPrice, currency, products, locale }) => ({
        url: `/paypro_createOrder`,
        method: 'POST',
        body: {
          totalPrice,
          currency,
          products,
          locale,
        },
      }),
    }),
    abandonCheckout: builder.mutation<IAbandonCheckoutResponse, IAbandonCheckoutRequest>({
      query: ({ lcOrderId }) => ({
        url: `/paypro_abandonCheckout`,
        method: 'POST',
        body: { lcOrderId },
      }),
    }),
    resumeCheckout: builder.mutation<IResumeCheckoutResponse, IResumeCheckoutRequest>({
      query: ({ lcOrderId }) => ({
        url: `/paypro_resumeCheckout`,
        method: 'POST',
        body: { lcOrderId },
      }),
    }),
    getOrderStatus: builder.query<IGetOrderStatusResponse, IGetOrderStatusRequest>({
      query: ({ orderId }) => ({
        url: `/getOrderStatus`,
        method: 'POST',
        body: { orderId },
      }),
    }),
    getPrice: builder.mutation<IGetPriceResponse, IGetPriceRequest>({
      query: ({ licenseType, quantity, currency, durationMonths }) => ({
        url: `/getPrice`,
        method: 'POST',
        body: {
          licenseType,
          quantity,
          currency,
          durationMonths,
        },
      }),
    }),
    getOrders: builder.mutation<IPaginationResponse<any>, IGetOrdersRequest>({
      query: ({ pageIndex, pageSize, sortBy, search }) => ({
        url: '/getOrders',
        method: 'POST',
        body: {
          pageIndex,
          pageSize,
          sortBy,
          search,
        },
      }),
    }),
    getOrdersSummary: builder.mutation<IOrdersSummary, void>({
      query: () => ({
        url: '/getOrdersSummary',
        method: 'POST',
        body: {},
      }),
    }),
    getOrderDetails: builder.mutation<IOrderDetails, IGetOrderDetailsRequest>({
      query: ({ lcOrderId }) => ({
        url: '/getOrderDetails',
        method: 'POST',
        body: { lcOrderId },
      }),
    }),
  }),
})

export const {
  useGetProgramForCompanyMutation,
  useCreateOrderPayproMutation,
  useAbandonCheckoutMutation,
  useResumeCheckoutMutation,
  useGetOrderStatusQuery,
  useGetPriceMutation,
  useGetOrdersMutation,
  useGetOrdersSummaryMutation,
  useGetOrderDetailsMutation,
} = purchasesApi
